import React, { useState, useEffect } from 'react';
import { useRescheduleJobMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { format } from 'date-fns';
import { isJobLocked } from '../../utils/lockHelpers';
import { validateDeliveryDate } from '../../utils/deliveryDateValidation';

const DATETIME_INPUT_CLASS =
  'form-input text-sm w-full min-w-[160px] border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed';

interface InlineDeliveryDateEditorProps {
  job: any;
  onUpdated: () => void;
}

export const InlineDeliveryDateEditor: React.FC<InlineDeliveryDateEditorProps> = ({
  job,
  onUpdated,
}) => {
  const [rescheduleJob, { isLoading }] = useRescheduleJobMutation();
  const toast = useToast();

  const locked = isJobLocked(job.lock_data);

  const savedDateTime = job.potential_delivery_date
    ? format(new Date(job.potential_delivery_date), "yyyy-MM-dd'T'HH:mm")
    : '';

  const [localValue, setLocalValue] = useState(savedDateTime);

  useEffect(() => {
    setLocalValue(savedDateTime);
  }, [savedDateTime, job.id]);

  const handleDateTimeChange = async (newDateTime: string) => {
    if (!newDateTime || newDateTime === savedDateTime) {
      return;
    }

    if (locked) {
      toast.warning('Locked', 'This job is locked and the delivery date cannot be changed');
      setLocalValue(savedDateTime);
      return;
    }

    const validationError = validateDeliveryDate({
      potentialDeliveryDate: newDateTime,
      manufactureDate: job.manufacture_date,
      glassArrivalDate: job.glass_arrival_date,
      jobStatus: job.status,
      productionStatus: job.production_status,
    });

    if (validationError) {
      toast.error('Date Validation Error', validationError);
      setLocalValue(savedDateTime);
      return;
    }

    try {
      const newDate = new Date(newDateTime);

      await rescheduleJob({
        id: job.id,
        potential_delivery_date: newDate.toISOString(),
      }).unwrap();

      toast.success(
        `Job #${job.website_job_number} rescheduled to ${format(newDate, 'dd/MM/yyyy HH:mm')}`
      );
      onUpdated();
    } catch (error: any) {
      setLocalValue(savedDateTime);
      toast.error(error?.data?.detail || 'Failed to update date');
    }
  };

  return (
    <input
      type="datetime-local"
      step="300"
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        void handleDateTimeChange(e.target.value);
      }}
      disabled={locked || isLoading}
      title={locked ? 'Job is locked - date cannot be changed' : undefined}
      className={DATETIME_INPUT_CLASS}
    />
  );
};
